import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, ViewEncapsulation, effect, inject, input, signal } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { dataToken } from '../../../core/abstractions/base.sub-resource';
import { CommandMapper } from '../../../core/utils/mappers/command.mapper';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { RelatedItemModel } from '../../../models/core/related-item.model';
import { TabLoadedInstanceModel } from '../../../models/core/tab-loaded-instance.model';
import { DictionaryType } from '../../../models/types/dictionary.type';
import { OptionalType } from '../../../models/types/optional.type';
import { ActionButtonsService } from '../../../services/action-buttons.service';
import { ControllerService } from '../../../services/controller.service';
import { speedDialButtons } from '../speed-dial-fab/speed-dial-fab.component';

export const relatedResources = signal<RelatedItemModel[]>([]);
export const currentResourceTab = signal<string>('');

@Component({
    selector: 'sl-form-view',
    templateUrl: './form-view.component.html',
    styleUrls: ['./form-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormViewComponent extends BaseComponent {
    mainPageActions = input.required<RelatedItemModel[]>();
    shouldDisplayHeader = input(true);

    currentTab: Portal<any>;
    readonly relatedResources = relatedResources;
    readonly isLoadingTab = signal(false);
    readonly tabIndex = signal(0);

    private loadedInstances: DictionaryType<TabLoadedInstanceModel> = {};
    private isExternalChange = false;
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly controllerService = inject(ControllerService);
    private readonly injector = inject(Injector);

    constructor() {
        super();
        effect(
            () => {
                if (!!currentResourceTab()) {
                    this.selectTabByCommand(currentResourceTab());
                }
            },
            { allowSignalWrites: true }
        );
    }

    tabChange(event: MatTabChangeEvent): void {
        if (!this.isExternalChange) {
            this.updateAddressBar(event.index);
        } else {
            this.isExternalChange = false;
        }

        speedDialButtons.set([]);

        // We check for index greater than zero, because the main tab is not a dynamic view.
        if (event.index > 0) {
            // -1 to account for the main tab not present in the related resources.
            this.loadTab(this.relatedResources()[event.index - 1]);
        } else {
            speedDialButtons.set(this.actionButtonsService.convert(this.mainPageActions()));
        }
    }

    private loadTab(subResource: RelatedItemModel): void {
        const loadedInstance = this.loadedInstances[subResource.commandName];

        // If the current tab was already loaded, we reuse it instead of creating a new one.
        if (!!loadedInstance) {
            this.reloadExistingComponent(loadedInstance);
            return;
        }

        this.isLoadingTab.set(true);

        // Load data from the server, if any, and create the sub resource.
        if (!CommandMapper.skipDefaultSubResourceBehavior.includes(subResource.commandName)) {
            this.controllerService.executeSubResource(subResource).subscribe(r => this.loadSubResource(r, subResource));
        } else {
            this.loadSubResource(undefined, subResource);
        }
    }

    private loadSubResource(response: OptionalType<ApiResponseModel<any>>, resource: RelatedItemModel): void {
        const component = CommandMapper.commands.find(c => c.name === resource.commandName)?.component;

        if (!component) {
            return;
        }

        const portal = new ComponentPortal(component, undefined, this.createInjector(response));
        this.loadedInstances[resource.commandName] = new TabLoadedInstanceModel({
            portal,
            actions: response?.relatedActions || []
        });
        this.currentTab = portal;

        if (!!response?.relatedActions) {
            speedDialButtons.set(this.actionButtonsService.convert(response.relatedActions));
        }

        this.isLoadingTab.set(false);
    }

    private reloadExistingComponent(loadedInstance: TabLoadedInstanceModel): void {
        this.currentTab = loadedInstance.portal;
        speedDialButtons.set(this.actionButtonsService.convert(loadedInstance.actions));
    }

    private createInjector(response: OptionalType<ApiResponseModel<any>>): Injector {
        return Injector.create({ providers: [{ provide: dataToken, useValue: response }], parent: this.injector });
    }

    private selectTabByCommand(command: string): void {
        const idx = this.relatedResources().findIndex(rr => rr.commandName === command);

        if (idx < 0) {
            return;
        }

        this.isExternalChange = true;

        this.tabIndex.set(idx + 1); // +1 to account for the main tab not present in the related resources.
    }

    private updateAddressBar(currentIndex: number): void {
        let subUrl = '../';

        if (currentIndex > 0) {
            const res = this.relatedResources()[currentIndex - 1];

            Object.keys(CommandMapper.tabs).forEach(key => {
                if (res.commandName === CommandMapper.tabs[key]) {
                    subUrl += key;
                    return;
                }
            });
        }

        const url = this.router.createUrlTree([subUrl], { relativeTo: this.activatedRoute }).toString();

        this.location.replaceState(url);
    }
}
